<script setup lang="ts">
import merge from 'lodash/merge';
import Multiselect from 'vue-multiselect/src/Multiselect.vue';

interface FormCategoriesMatching
{
    content: {
        value: {
            description: string;
            categories: Category[];
            items: Item[];
        };
    };
}

interface Category
{
    id: number;
    name: string;
}

interface Item
{
    id: number;
    name: string;
    categoryId: number;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const { $t } = useLocalization();
const { $alert } = useAlerts();

const defaults: any = {
    description: '',
    categories: [] as Category[],
    items: [] as Item[]
};

const form = computed((): FormType<FormCategoriesMatching> =>
{
    return props.modelValue;
});

const categoriesOptions = computed((): Record<number, string> =>
{
    const categories = form.value.content.value.categories.map((p) =>
    {
        return {
            [p.id]: p.name
        };
    });

    return categories.reduce((result: Record<number, string>, item) =>
    {
        const key = Object.keys(item)[0];

        result[key as any] = item[key as any];

        return result;
    }, {} as Record<number, string>);
});

function addCategory(): void
{
    const ids = form.value.content.value.categories.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.categories.length > 0 ? Math.max(...ids) : 1;

    const lastIndex = form.value.content.value.categories.length - 1;

    if (form.value.content.value.categories[lastIndex].name != '')
    {
        form.value.content.value.categories.push({
            id: maxId + 1,
            name: ''
        });
    }
    else
    {
        $alert.warning($t('[[[Nie podano nazwy poprzedniej kategorii.]]]'));
    }
}

function removeCategory(index: number): void
{
    const id = form.value.content.value.categories[index].id;
    let canDelete = true;

    form.value.content.value.items.forEach((item: Item) =>
    {
        if (item.categoryId == id && canDelete)
        {
            $alert.warning($t('[[[Nie można usunąć kategorii do ktorej są przypisane elementy.]]]'));
            canDelete = false;
        }
    });

    if (canDelete)
    {
        const items = form.value.content.value.categories.slice();

        items.splice(index, 1);
        form.value.content.value.categories = items;

        form.value.$errors.clear();
    }
}

function addItem(): void
{
    const ids = form.value.content.value.items.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.items.length > 0 ? Math.max(...ids) : 1;

    const lastIndex = form.value.content.value.items.length - 1;

    if (form.value.content.value.items[lastIndex].name != '' && form.value.content.value.items[lastIndex].categoryId != null)
    {
        form.value.content.value.items.push({
            id: maxId + 1,
            name: '',
            categoryId: 0
        });

        form.value.$errors.clear();
    }
    else
    {
        $alert.warning($t('[[[Nie podano nazwy poprzedniego elementu.]]]'));
    }
}

function removeItem(index: number): void
{
    const items = form.value.content.value.items.slice();

    items.splice(index, 1);
    form.value.content.value.items = items;

    form.value.$errors.clear();
}

function triggetModel(): any
{
    return form.value;
}

onBeforeMount((): void =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.categories.length)
    {
        form.value.content.value.categories = [{
            id: 1,
            name: ''
        }];
    }

    if (!form.value.content.value.items.length)
    {
        form.value.content.value.items = [{
            id: 1,
            name: '',
            categoryId: null
        }];
    }
});

watch(() => form.value.content.value.categories, (value: any): void =>
{
    emit('update:modelValue', triggetModel());
}, { deep: true });

watch(() => form.value.content.value.items, (value: any, oldValue: any): void =>
{
    form.value.content.value.items.forEach((element, index) =>
    {
        if (form.value.$errors.has(`content.value.items[${index}].categoryid`) && element.categoryId)
            form.value.$errors.clear(`content.value.items[${index}].categoryid`);
    });

    emit('update:modelValue', triggetModel());
}, { deep: true });

function answerUp(index: number): void
{
    if (index > 0)
    {
        const answers = form.value.content.value.categories.splice(index, 1);

        form.value.content.value.categories.splice(index - 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}

function answerDown(index: number): void
{
    if (index < form.value.content.value.categories.length - 1)
    {
        const answers = form.value.content.value.categories.splice(index, 1);

        form.value.content.value.categories.splice(index + 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('content')" :state="form.$errors.state('content')">
        <RichTextEditor
            :id="seconds('description-' + form.id)"
            v-model="form.content.value.description"
            name="description"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Kategorie]]]')" :invalid-feedback="form.$errors.first('content.value.categories')" :state="form.$errors.state('content.value.categories')" required>
        <div v-for="(category, i) in form.content.value.categories" :key="i" class="bg-light p-2 mb-3">
            <div class="input-group">
                <div class="input-group-prepend">
                    <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}</span>
                </div>
                <IdeoFormInput v-model="category.name" type="text" :name="`content.value.categories[${i}].name`" :placeholder="$t('[[[Nazwa kategorii]]]')" :data-cy="`category-${i}`" />
                <div class="input-group-append me-2">
                    <ActionButton
                        variant="danger"
                        icon="far fa-trash"
                        :title="$t('[[[Usuń kategorię]]]')"
                        :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                        :disabled="form.content.value.categories.length < 2"
                        @click="removeCategory(i)"
                    />
                </div>
                <ActionButton
                    variant="secondary"
                    icon="far fa-arrow-up"
                    class="ms-auto"
                    :title="$t('[[[Wyżej]]]') "
                    :disabled="i == 0"
                    @click="answerUp(i)"
                />
                <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="i == form.content.value.categories.length - 1" @click="answerDown(i)" />
            </div>
            <small v-if="!form.$errors.state(`content.value.categories[${i}].name`)" class="form-text text-danger">{{ form.$errors.first(`content.value.categories[${i}].name`) }}</small>
        </div>
        <div class="row">
            <div class="col-12">
                <ActionButton
                    variant="outline-dark"
                    class="w-100"
                    icon="far fa-plus"
                    :text="$t('[[[Dodaj kategorię]]]')"
                    data-cy="addCategory"
                    @click="addCategory()"
                />
            </div>
        </div>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Elementy]]]')" :invalid-feedback="form.$errors.first('content.value.items')" :state="form.$errors.state('content.value.items')" required>
        <div v-for="(item, i) in form.content.value.items" :key="i" class="bg-light p-2 mb-3">
            <div class="input-group elements-que">
                <div class="input-group-prepend">
                    <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}</span>
                </div>
                <IdeoFormInput v-model="item.name" type="text" :name="`content.value.items[${i}].name`" :placeholder="$t('[[[Nazwa elementu]]]')" :data-cy="`answer-${i}`" />
                <Multiselect
                    v-model="item.categoryId"
                    :name="`content.value.items[${i}].categoryId`"
                    :options="Object.keys(categoriesOptions)"
                    :custom-label="(opt: any) => categoriesOptions[opt]"
                    :multiple="false"
                    :deselect-label="''"
                    :select-label="''"
                    :selected-label="''"
                    :data-cy="`answer-${i}-category`"
                    :placeholder="$t('[[[Wybierz kategorię]]]')"
                >
                    <template #noOptions>
                        {{ $t('[[[Lista jest pusta]]]') }}
                    </template>
                    <template #noResult>
                        {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
                    </template>
                </Multiselect>
                <div class="input-group-append">
                    <ActionButton
                        variant="danger"
                        icon="far fa-trash"
                        :title="$t('[[[Usuń element]]]')"
                        :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                        :disabled="form.content.value.categories.length < 2"
                        @click="removeItem(i)"
                    />
                </div>
            </div>
            <small v-if="!form.$errors.state(`content.value.items[${i}].name`)" class="form-text text-danger">{{ form.$errors.first(`content.value.items[${i}].name`) }}</small>
            <small v-if="!form.$errors.state(`content.value.items[${i}].categoryId`)" class="form-text text-danger">{{ form.$errors.first(`content.value.items[${i}].categoryId`) }}</small>
        </div>
        <small v-if="!form.$errors.state(`content.value.categories.count`)" class="form-text text-danger">{{ form.$errors.first(`content.value.categories.count`) }}</small>
        <div class="row">
            <div class="col-12">
                <ActionButton
                    variant="outline-dark"
                    class="w-100"
                    icon="far fa-plus"
                    :text="$t('[[[Dodaj element]]]')"
                    data-cy="addAnswer"
                    @click="addItem()"
                />
            </div>
        </div>
    </IdeoFormGroup>
</template>

<style lang="scss" scoped>
.elements-que {
    display: grid;
    grid-template-columns: 1fr 1fr;

    @media only screen and (min-width: 960px)
    {
        grid-template-columns: 34px 1fr 1fr 38px;
    }

    :deep(.form-control) {
        width: 100%;
    }

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.multiselect__tags) {
        border-radius: 0;
    }
}
</style>
